import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Typography,
  useTheme,
  Box,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import axios from "axios";
import config from "../../config";
import { NumericFormat } from "react-number-format";
const promotionTypes = [
  "basic",
  "flash",
  "vip",
  "combo",
  "quantity",
  "birthday",
  "category",
];

const initialForm = {
  productId: "",
  categoryId: "",
  discountValue: "",
  discountType: "percent",
  type: "basic",
  condition: {},
  startDate: dayjs(),
  endDate: dayjs().add(7, "day"),
};

const AddPromotionModal = ({ open, onClose }) => {
  const theme = useTheme();
  const labelColor = theme.palette.mode === "dark" ? "#fff" : "#000";

  const [formData, setFormData] = useState(initialForm);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (open) {
      setFormData({ ...initialForm });
      setError("");
      fetchProducts();
      fetchCategories();
    }
  }, [open]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/Products`);
      setProducts(res.data || []);
    } catch (err) {
      console.error("Lỗi tải sản phẩm:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/Categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error("Lỗi tải danh mục:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    const defaultCondition = {
      flash: { flashStart: "", flashEnd: "" },
      vip: { minLevel: "" },
      combo: { requiredProductIds: [] },
      quantity: { tiers: [{ min: "", discount: "" }] },
      birthday: { daysBefore: "", daysAfter: "" },
    };

    setFormData((prev) => ({
      ...prev,
      type: newType,
      condition: defaultCondition[newType] || {},
    }));
  };

  const handleConditionChange = (key, value, index = null) => {
    setFormData((prev) => {
      const newCondition = { ...prev.condition };
      if (prev.type === "quantity" && index !== null) {
        newCondition.tiers[index][key] = value;
      } else if (prev.type === "combo" && key === "requiredProductIds") {
        newCondition.requiredProductIds = value
          .split(",")
          .map((id) => parseInt(id.trim()))
          .filter(Boolean);
      } else {
        newCondition[key] = value;
      }
      return {
        ...prev,
        condition: newCondition,
      };
    });
  };

  const renderConditionFields = () => {
    const { type, condition } = formData;
    switch (type) {
      case "flash":
        return (
          <>
            <TextField
              label="Thời gian bắt đầu"
              value={condition.flashStart || ""}
              onChange={(e) =>
                handleConditionChange("flashStart", e.target.value)
              }
              fullWidth
              placeholder="VD: 2025-04-13T12:00"
              InputLabelProps={{ style: { color: labelColor } }}
            />
            <TextField
              label="Thời gian kết thúc"
              value={condition.flashEnd || ""}
              onChange={(e) =>
                handleConditionChange("flashEnd", e.target.value)
              }
              fullWidth
              sx={{ mt: 1 }}
              placeholder="VD: 2025-04-13T18:00"
              InputLabelProps={{ style: { color: labelColor } }}
            />
          </>
        );
      case "vip":
        return (
          <TextField
            label="Cấp độ VIP"
            value={condition.minLevel || ""}
            onChange={(e) => handleConditionChange("minLevel", e.target.value)}
            fullWidth
            InputLabelProps={{ style: { color: labelColor } }}
          />
        );
      case "combo":
        return (
          <TextField
            label="Mã sản phẩm yêu cầu"
            value={(condition.requiredProductIds || []).join(",")}
            onChange={(e) =>
              handleConditionChange("requiredProductIds", e.target.value)
            }
            fullWidth
            placeholder="VD: 1, 2, 3"
            InputLabelProps={{ style: { color: labelColor } }}
          />
        );
      case "quantity":
        return (
          <>
            {condition.tiers?.map((tier, index) => (
              <Box key={index} sx={{ display: "flex", gap: 2, mt: 1 }}>
                <TextField
                  label={`Tối thiểu mua #${index + 1}`}
                  value={tier.min}
                  onChange={(e) =>
                    handleConditionChange("min", e.target.value, index)
                  }
                  type="number"
                  fullWidth
                  InputLabelProps={{ style: { color: labelColor } }}
                />
                <TextField
                  label="Giảm (%)"
                  value={tier.discount}
                  onChange={(e) =>
                    handleConditionChange("discount", e.target.value, index)
                  }
                  type="number"
                  fullWidth
                  InputLabelProps={{ style: { color: labelColor } }}
                />
              </Box>
            ))}
            <Button
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  condition: {
                    ...prev.condition,
                    tiers: [...prev.condition.tiers, { min: "", discount: "" }],
                  },
                }))
              }
              sx={{ mt: 1 }}
            >
              + Thêm mức giảm
            </Button>
          </>
        );
      case "birthday":
        return (
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Số ngày trước sinh nhật"
              value={condition.daysBefore || ""}
              onChange={(e) =>
                handleConditionChange("daysBefore", e.target.value)
              }
              type="number"
              fullWidth
              InputLabelProps={{ style: { color: labelColor } }}
            />
            <TextField
              label="Số ngày sau sinh nhật"
              value={condition.daysAfter || ""}
              onChange={(e) =>
                handleConditionChange("daysAfter", e.target.value)
              }
              type="number"
              fullWidth
              InputLabelProps={{ style: { color: labelColor } }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const validate = () => {
    const { discountValue, discountType, type, condition, startDate, endDate } =
      formData;

    if (!formData.productId && !formData.categoryId) {
      return "⚠️ Cần ít nhất ProductId hoặc CategoryId.";
    }

    if (!discountValue || isNaN(discountValue)) {
      return "⚠️ Giá trị giảm không hợp lệ.";
    }

    if (!["percent", "amount"].includes(discountType)) {
      return "⚠️ Loại giảm giá không hợp lệ.";
    }

    if (discountType === "percent" && discountValue > 100) {
      return "⚠️ Giảm giá phần trăm không được vượt quá 100%.";
    }

    if (endDate.isBefore(startDate)) {
      return "⚠️ Ngày kết thúc không được trước ngày bắt đầu.";
    }

    if (type === "flash") {
      if (!condition.flashStart || !condition.flashEnd) {
        return "⚠️ Vui lòng nhập thời gian bắt đầu và kết thúc cho flash.";
      }
    }

    if (type === "vip" && !condition.minLevel) {
      return "⚠️ Vui lòng nhập cấp độ VIP tối thiểu.";
    }

    if (
      type === "combo" &&
      (!condition.requiredProductIds ||
        condition.requiredProductIds.length === 0)
    ) {
      return "⚠️ Vui lòng nhập ít nhất một mã sản phẩm combo.";
    }

    if (type === "quantity") {
      if (!Array.isArray(condition.tiers) || condition.tiers.length === 0) {
        return "⚠️ Vui lòng thêm ít nhất một tier giảm giá.";
      }
      for (const tier of condition.tiers) {
        if (!tier.min || !tier.discount) {
          return "⚠️ Mỗi tier cần có số lượng tối thiểu và phần trăm giảm.";
        }
      }
    }

    if (
      type === "birthday" &&
      (condition.daysBefore === "" || condition.daysAfter === "")
    ) {
      return "⚠️ Vui lòng nhập số ngày trước và sau sinh nhật.";
    }

    return null;
  };

  const handleSubmit = async () => {
    setError("");
    const validationMessage = validate();
    if (validationMessage) {
      return setError(validationMessage);
    }

    try {
      const payload = {
        productId: formData.productId ? parseInt(formData.productId) : null,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        discountValue: parseFloat(formData.discountValue),
        discountType: formData.discountType,
        type: formData.type,
        conditionJson: Object.keys(formData.condition).length
          ? JSON.stringify(formData.condition)
          : null,
        startDate: formData.startDate.format("YYYY-MM-DD"),
        endDate: formData.endDate.format("YYYY-MM-DD"),
      };

      await axios.post(`${config.apiBaseUrl}/Discounts`, payload);
      alert("✅ Tạo khuyến mãi thành công!");
      onClose();
    } catch (err) {
      const msg = err?.response?.data;
      setError(typeof msg === "string" ? `❌ ${msg}` : "❌ Đã xảy ra lỗi.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Thêm khuyến mãi</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                options={products}
                getOptionLabel={(option) => option.name || ""}
                onChange={(_, value) =>
                  setFormData((prev) => ({
                    ...prev,
                    productId: value?.productId || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chọn sản phẩm"
                    fullWidth
                    InputLabelProps={{ style: { color: labelColor } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={categories}
                getOptionLabel={(option) => option.name || ""}
                onChange={(_, value) =>
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: value?.categoryId || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chọn danh mục"
                    fullWidth
                    InputLabelProps={{ style: { color: labelColor } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <NumericFormat
                customInput={TextField}
                label="Giá trị giảm"
                value={formData.discountValue}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={0}
                allowNegative={false}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  setFormData((prev) => ({
                    ...prev,
                    discountValue: floatValue || 0,
                  }));
                }}
                fullWidth
                InputLabelProps={{ style: { color: labelColor } }}
              />
            </Grid>
            {formData.productId &&
              (() => {
                const selectedProduct = products.find(
                  (p) => p.productId === parseInt(formData.productId)
                );
                const basePrice = selectedProduct?.basePrice || 0;
                const discount = parseFloat(formData.discountValue || 0);
                let finalPrice = basePrice;

                if (formData.discountType === "percent") {
                  finalPrice = basePrice - (basePrice * discount) / 100;
                } else if (formData.discountType === "amount") {
                  finalPrice = basePrice - discount;
                }

                return (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1 }}>
                      <Typography>
                        💰 <strong>Giá gốc:</strong>{" "}
                        {basePrice.toLocaleString()} đ
                      </Typography>
                      <Typography>
                        ✅ <strong>Giá sau giảm:</strong>{" "}
                        <span style={{ color: "green" }}>
                          {Math.max(finalPrice, 0).toLocaleString()} đ
                        </span>
                      </Typography>
                    </Box>
                  </Grid>
                );
              })()}

            <Grid item xs={6}>
              <TextField
                select
                label="Loại giảm giá"
                name="discountType"
                value={formData.discountType}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ style: { color: labelColor } }}
              >
                <MenuItem value="percent">Phần trăm (%)</MenuItem>
                <MenuItem value="amount">Số tiền</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Loại khuyến mãi"
                name="type"
                value={formData.type}
                onChange={handleTypeChange}
                fullWidth
                InputLabelProps={{ style: { color: labelColor } }}
              >
                {promotionTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {formData.type !== "basic" && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Điều kiện áp dụng
                </Typography>
                {renderConditionFields()}
              </Grid>
            )}

            <Grid item xs={6}>
              <DatePicker
                label="Ngày bắt đầu"
                value={formData.startDate}
                onChange={(newValue) =>
                  setFormData((prev) => ({ ...prev, startDate: newValue }))
                }
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { style: { color: labelColor } },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="Ngày kết thúc"
                value={formData.endDate}
                onChange={(newValue) =>
                  setFormData((prev) => ({ ...prev, endDate: newValue }))
                }
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { style: { color: labelColor } },
                  },
                }}
              />
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPromotionModal;
